import React, { useState } from 'react';
import {
  Grid,
  GridCellProps,
  GridColumn as Column,
  GridDataStateChangeEvent,
  GridNoRecords,
  GridDetailRowProps,
  GridDetailExpandChangeEvent
} from '@progress/kendo-react-grid';
import {
  DataResult,
  State,
  toDataSourceRequestString
} from '@progress/kendo-data-query';
import { ColumnMenu } from '../components/columnMenu';
import { ExcelExport } from '@progress/kendo-react-excel-export';
import {
  GridToolBar,
  GridToolbarDataStateChangeEvent
} from '../components/grid/grid-tool-bar';
import { DefaultGridSettings } from '../constants/grid-settings';
import { NoRecords } from '../components/grid/no-records';
import { formatDateToDDMMYYYY } from '../utils/date';
import EventsScheduler from './events-scheduler';
import { ExpandedEvent } from '../types/event';
import Toolbar from '../components/page-toolbar';
import { useGetExpandedEventsAsAdmin } from '../hooks/services/use-events';
import { Loader } from '@progress/kendo-react-indicators';

const initialDataState: State = {
  sort: [{ field: 'startDateUtc', dir: 'desc' }],
  filter: DefaultGridSettings.initialDataState.filter,
  skip: DefaultGridSettings.initialDataState.skip,
  group: DefaultGridSettings.initialDataState.group
};
interface ActivityData {
  startDateUtc?: Date;
  endDateUtc?: Date;
  name?: string;
  code?: string;
  type?: string;
  pax?: string;
  status?: string;
}

const DetailComponent = (props: GridDetailRowProps) => {
  const dataItem = props.dataItem as ExpandedEvent;
  const assessmentData: ActivityData[] = dataItem.eventPlaylists
    .filter((item) => item.assessmentName)
    .map((item) => ({
      startDateUtc: item.startDateUtc,
      endDateUtc: item.endDateUtc,
      code: item.code,
      pax: item.approxPax,
      name: item.assessmentName,
      type: 'Assessment',
      status: item.status
    }));

  const gameData: ActivityData[] = dataItem.eventPlaylists
    .filter((item) => item.gameName)
    .map((item) => ({
      startDateUtc: item.startDateUtc,
      endDateUtc: item.endDateUtc,
      code: item.code,
      pax: item.approxPax,
      name: item.gameName,
      type: 'Game',
      status: item.status
    }));

  const feedbackData: ActivityData[] = dataItem.eventFeedbacks.map((item) => ({
    startDateUtc: item.startDateUtc,
    endDateUtc: item.endDateUtc,
    code: item.code,
    name: item.name,
    type: 'Feedback',
    status: item.status
  }));

  const data = [...assessmentData, ...gameData, ...feedbackData];

  return (
    <>
      {data.length !== 0 && (
        <table className="k-table k-grid-header-table k-table-md event-detail-table">
          <thead className="k-table-thead">
            <tr className="k-table-row">
              <th className=" k-table-th k-header k-sorted table-header">
                Start Date
              </th>
              <th className="k-table-th k-header k-sorted table-header">
                End Date
              </th>
              <th className="k-table-th k-header k-sorted table-header">
                Name
              </th>
              <th className="k-table-th k-header k-sorted table-header">
                Type
              </th>
              <th className="k-table-th k-header k-sorted table-header">
                Code
              </th>
              <th className="k-table-th k-header k-sorted table-header">Pax</th>
              <th className="k-table-th k-header k-sorted table-header">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={`row-${index}`} className="table-row">
                <td className="table-cell">
                  {formatDateToDDMMYYYY(item.startDateUtc!, true)}
                </td>
                <td className="table-cell">
                  {formatDateToDDMMYYYY(item.endDateUtc!, true)}
                </td>
                <td className="table-cell">{item.name}</td>
                <td className="table-cell">{item.type}</td>
                <td className="table-cell">{item.code}</td>
                <td className="table-cell">{item.pax}</td>
                <td className="table-cell">{item.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

const EventsManagementGrid = () => {
  const [dataState, setDataState] = useState<State>({
    ...initialDataState,
    take: DefaultGridSettings.initialDataState.take
  });
  const [gridMode, setGridMode] = useState<string>('grid');
  const [expandState, setExpandState] = useState<{ [id: string]: boolean }>({});

  const { data, isLoading, mutate } = useGetExpandedEventsAsAdmin(
    toDataSourceRequestString(dataState)
  );

  const gridExportRef = React.useRef<ExcelExport | null>(null);

  const DateCell = (props: GridCellProps) => {
    return (
      <td className={'k-table-td'}>
        {formatDateToDDMMYYYY(props.dataItem.startDateUtc, true)}
      </td>
    );
  };

  const expandChange = (event: GridDetailExpandChangeEvent) => {
    setExpandState(event.detailExpand);
  };

  return (
    <>
      <Toolbar title="Catalyst Portal Events" />
      <div
        className={'pt-2'}
        style={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          width: '100%',
          overflow: 'hidden'
        }}>
        <GridToolBar
          searchPlaceholder={'Search events'}
          columnsToSearch={['name', 'organisation']}
          showCalendarMode={true}
          showCardMode={false}
          exportRef={gridExportRef}
          onGridModeChange={(e) => {
            const newState = {
              ...initialDataState
            };
            if (e.gridMode === 'grid') {
              newState.take = DefaultGridSettings.initialDataState.take;
            }
            setDataState(newState);
            setGridMode(e.gridMode);
          }}
          {...dataState}
          onDataStateChange={(e: GridToolbarDataStateChangeEvent) => {
            setDataState(e.dataState);
          }}></GridToolBar>
        <ExcelExport
          data={data?.data}
          ref={gridExportRef}
          fileName={`all-events.xlsx`}>
          {gridMode === 'grid' && (
            <Grid
              id={'grid-events'}
              style={{
                flexGrow: 1,
                minHeight: 0,
                height: '100%',
                overflow: 'hidden'
              }}
              dataItemKey={'id'}
              pageable={DefaultGridSettings.pagerSettings}
              sortable={true}
              data={data}
              {...dataState}
              onDataStateChange={(e: GridDataStateChangeEvent) => {
                setDataState(e.dataState);
              }}
              detail={DetailComponent}
              detailExpand={expandState}
              onDetailExpandChange={expandChange}>
              <Column
                field={'startDateUtc'}
                title={'Start date'}
                cells={{ data: DateCell }}
              />
              <Column
                field={'endDateUtc'}
                title={'End date'}
                cells={{ data: DateCell }}
              />
              <Column field={'name'} title={'Name'} columnMenu={ColumnMenu} />
              <Column field={'type'} title={'Type'} />
              <Column
                field={'organisation'}
                title={'Organisation'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'approxPax'}
                title={'Pax'}
                columnMenu={ColumnMenu}
              />
              <Column
                field={'status'}
                title={'Status'}
                columnMenu={ColumnMenu}
              />

              <GridNoRecords>
                {isLoading ? (
                  <div
                    className={
                      'd-flex flex-column align-items-center gap-2 text-muted'
                    }>
                    <Loader type={'pulsing'} />
                    <span>Loading data</span>
                  </div>
                ) : (
                  <NoRecords />
                )}
              </GridNoRecords>
            </Grid>
          )}
          {gridMode === 'cal' && (
            <EventsScheduler eventData={data as DataResult} />
          )}
        </ExcelExport>
      </div>
    </>
  );
};
export default EventsManagementGrid;
